import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import React, {useState} from "react";
import {
	Box,
	Breadcrumbs, Button,
	CircularProgress,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {cloneMutationData} from "../Utils";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {TabPanel} from "../../../components/common/TabPanel";
import {translateFunction} from "../../../localization/TranslationService";
import {DELETE_CUSTOM_ATTRIBUTES_SETUP, EXISTS_CUSTOM_ATTRIBUTES_SETUP, GET_CUSTOM_ATTRIBUTES_SETUP, PUT_CUSTOM_ATTRIBUTES_SETUP} from "../Queries";
import {productsEditorTargetStage} from "../ProductModuleOverview";
import {CustomAttributeSetup} from "../models/CustomAttributeSetup";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const CustomAttributeSetupDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.id;
	const [deleted, setDeleted] = useState<boolean>(false);
	const {loading, data, error} = useQuery(GET_CUSTOM_ATTRIBUTES_SETUP, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage,
			id: id,
		}
	});
	
	const [customAttributeSetup, setCustomAttributeSetup] = useState<CustomAttributeSetup>();
	
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = data.customAttributeSetup.nodes;
	if (nodes === null || nodes.length === 0)
		throw new Error(`CustomAttributeSetup with id ${id} was not found.`);
	
	const node: CustomAttributeSetup = nodes[0];
	if (!customAttributeSetup) {
		setCustomAttributeSetup(node);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	
	const props = {customAttributeSetup: customAttributeSetup, setCustomAttributeSetup: setCustomAttributeSetup};
	
	const handleSave = async (existingItem: CustomAttributeSetup) => {
		if (!existingItem.description || existingItem.description === "")
			throw new Error(translate(`Description field needs a value`));
		
		if (!existingItem.mapping || existingItem.mapping === "")
			throw new Error(translate(`Mapping field needs a value`));
		
		await exists(existingItem).then(async (x) => {
			if (x) {
				throw new Error(translate(`Item already exists with the same description or mapping value`));
			} else
				await saveChanges(existingItem);
		});
	};
	const handleDelete = async (existingItem: CustomAttributeSetup) => {
		if (deleted)
			return;
		setDeleted(true);
		await deleteAttribute(existingItem).then(async (x) => {
			if (x.errors && x.errors.length > 0) {
				throw new Error(translate(x.errors[0].toString()));
			}
			if (x.data.errors && x.data.errors.length > 0) {
				throw new Error(translate(x.data.errors[0].message));
			}
		});
		
	};
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products/custom_attribute_setup">{translate("Custom attributes setup")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information {...props}/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<Stack gap={1} direction={"row"} >
						{!deleted &&
							<ProcessButton
								defaultText={translate("Save")}
								inProgressText={translate("Saving")}
								completedText={translate("Saved")}
								resetKeys={[customAttributeSetup]}
								variant={"outlined"}
								onClick={() => handleSave(customAttributeSetup)}/>
						}
						<ProcessButton
							defaultText={translate("Delete")}
							inProgressText={translate("Deleting")}
							completedText={translate("Deleted")}
							resetKeys={[customAttributeSetup]}
							variant={"outlined"}
							onClick={() => handleDelete(customAttributeSetup)}/>
						<Button variant={"outlined"} component={Link} to="/products/custom_attribute_setup">
							{translate("Back")}
						</Button>
					</Stack>
				</div>
			</Stack>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function exists(item: CustomAttributeSetup): Promise<boolean> {
	
	const existsItems = await graphQLServices.productsClient.query(
		{
			query: EXISTS_CUSTOM_ATTRIBUTES_SETUP,
			variables: {
				stage: productsEditorTargetStage,
				description: item.description,
				mapping: item.mapping
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.customAttributeSetup.nodes;
	
	for (const item1 of nodes) {
		if (item1.id === item.id) {
			continue;
		}
		return true;
	}
	
	return false;
}

async function saveChanges(item: CustomAttributeSetup) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_CUSTOM_ATTRIBUTES_SETUP,
		variables: {
			stage: productsEditorTargetStage,
			customAttributeSetup: cloneMutationData(item),
		}
	});
}

async function deleteAttribute(item: CustomAttributeSetup) {
	return await graphQLServices.productsClient.mutate({
		mutation: DELETE_CUSTOM_ATTRIBUTES_SETUP,
		variables: {
			stage: productsEditorTargetStage,
			id: item.id,
		}
	});
}

interface CustomAttributeSetupProps {
	readonly customAttributeSetup: CustomAttributeSetup;
	readonly setCustomAttributeSetup: (customAttributeSetup: CustomAttributeSetup) => void;
}

function Information(props: CustomAttributeSetupProps) {
	return <TabPanel value={0} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>{translate("Custom attribute setup Information")}</div>
				<TextField
					disabled={true}
					label={translate("Description")}
					value={props.customAttributeSetup.description}
					variant={"standard"}
				/>
				<TextField
					label={translate("Mapping")}
					value={props.customAttributeSetup.mapping}
					variant={"standard"}
					onChange={event => props.setCustomAttributeSetup({...props.customAttributeSetup, mapping: event.target.value})}
				/>
			</Stack>
		</div>
	</TabPanel>;
}

export function linkCustomAttributeSetupDetailsFor(id: string): string {
	return `/products/custom_attribute_setup/${encodeURIComponent(id)}`;
}

